import { useDataTableContext } from "./DataTableProvider";
import { memo, useEffect } from "react";
import { isTerminalTaskStatus } from "utils/isTerminalTaskStatus";
import { isAnalysisTable, isWorkflowTable } from "./DataTableProvider.helpers";
import { isTerminalRunStatus } from "utils/isTerminalRunStatus";

/**
 * Component that refetches analysis table rows that are currently executing.
 *
 * I chose to use a null component over a hook to minimize rerenders. Using a
 * hook would require the component calling it to rerender every time new
 * analyses are executed.
 */
export const DataTableAnalysisPoller = memo(function DataTableAnalysisPoller() {
  const refetchRows = useDataTableContext((s) => s.refetchAnalysisTableRows);
  const refetchWorkflowRows = useDataTableContext(
    (s) => s.refetchWorkflowTableRows,
  );

  // Determine whether any analysis table rows are executing
  const isPendingAnalysisRows = useDataTableContext((s) => {
    for (const table of s.tables.filter(isAnalysisTable)) {
      for (const row of table.rows) {
        if (row.task !== null && !isTerminalTaskStatus(row.task.status)) {
          return true;
        }
      }
    }

    return false;
  });

  const isPendingWorkflowRows = useDataTableContext((s) => {
    for (const table of s.tables.filter(isWorkflowTable)) {
      for (const row of table.rows) {
        if (row.run !== null && !isTerminalRunStatus(row.run.status)) {
          return true;
        }
      }
    }

    return false;
  });

  // Poll for updates to pending analysis table rows
  useEffect(() => {
    let timer: NodeJS.Timer;

    if (isPendingAnalysisRows) {
      timer = setInterval(() => {
        void refetchRows();
      }, 5000);
    }

    return () => clearInterval(timer);
  }, [isPendingAnalysisRows, refetchRows]);

  useEffect(() => {
    let timer: NodeJS.Timer;

    if (isPendingWorkflowRows) {
      timer = setInterval(() => {
        void refetchWorkflowRows();
      }, 5000);
    }

    return () => clearInterval(timer);
  }, [isPendingWorkflowRows, refetchRows, refetchWorkflowRows]);
  return null;
});
